import SideSheetModal from "@/components/SideSheetModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useRecentlyVisited,
  RecentlyVisitedItem,
  RecentType,
  inferTypeByUrl,
  matchesRecentFilter
} from "@/hooks/useRecentlyVisited";
import { DateTime } from "luxon";
import AvatarDetails from "@/components/AvatarDetails";
import { MaterialNames } from "@/components/material_symbol_names";
import Chip from "@/components/Chip";
import EmptyState from "@/components/EmptyState";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import Icon from "@/components/Icon";
import TextField from "@/components/TextField";
import { useAlgoliaSearch } from "@/components/AlgoliaSearch/useAlgoliaSearch";
import AlgoliaSearchResults from "@/components/AlgoliaSearch/AlgoliaSearchResults";
import {
  type GlobalHit,
  getGlobalDisplayName,
  renderGlobalHit,
  createGlobalSearchUiState,
  isGlobalExactMatch,
  filterGlobalSearchHits,
  navigateGlobalHit,
  createActivityAdditionalSearch,
  GLOBAL_QUICK_SEARCH_ACTIVITY_SLOTS
} from "@/components/AlgoliaSearch";
import { LookupPersonInCcms, parsePgaIdQuery, useCanShowCcmsMemberLookup } from "@/features/person/ccmsLookup";
import { useNavigate, Path } from "@/router";
import useAmplitude from "@/hooks/useAmplitude";
import { GLOBAL_SEARCH_ANALYTICS_EVENTS } from "./globalSearchAmplitude";

type FilterType = "all" | RecentType;

function getRecentItemObjectType(item: RecentlyVisitedItem): string {
  return item.type || inferTypeByUrl(item.url);
}

function RecentlyVisited() {
  const { trackEvent } = useAmplitude();
  const { getRecentlyVisitedItems } = useRecentlyVisited();
  const items = getRecentlyVisitedItems();
  const [filter, setFilter] = useState<FilterType>("all");

  function inferType(item: RecentlyVisitedItem): RecentType {
    return (item.type as RecentType) || inferTypeByUrl(item.url);
  }

  function handleFilterClick(nextFilter: FilterType) {
    trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchFilter, {
      backofficeObjectType: nextFilter
    });
    setFilter(nextFilter);
  }

  function handleRecentItemClick(item: RecentlyVisitedItem) {
    trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchRecent, {
      backofficeObjectType: getRecentItemObjectType(item)
    });
  }

  function getFilterDisplayName(filterType: FilterType): string {
    switch (filterType) {
      case "all":
        return "Items";
      case "people":
        return "People";
      case "facilities":
        return "Facilities";
      case "jobs":
        return "Jobs";
      case "activities":
        return "Activities";
      default:
        return "Items";
    }
  }

  const filteredItems = useMemo(() => {
    return items.filter((i) => matchesRecentFilter(inferType(i), filter));
  }, [items, filter]);

  return (
    <div className="mt-6">
      <SubSectionHeading className="mb-4">Recently Visited</SubSectionHeading>
      <div className="space-y-4">
        <div className="mb-2 flex gap-2 flex-wrap">
          <Chip
            label="All"
            selected={filter === "all"}
            color="strong"
            showCheckmark={false}
            onClick={() => handleFilterClick("all")}
          />
          <Chip
            label="People"
            selected={filter === "people"}
            color="strong"
            showCheckmark={false}
            onClick={() => handleFilterClick("people")}
          />
          <Chip
            label="Facilities"
            selected={filter === "facilities"}
            color="strong"
            showCheckmark={false}
            onClick={() => handleFilterClick("facilities")}
          />
          <Chip
            label="Jobs"
            selected={filter === "jobs"}
            color="strong"
            showCheckmark={false}
            onClick={() => handleFilterClick("jobs")}
          />
          <Chip
            label="Activities"
            selected={filter === "activities"}
            color="strong"
            showCheckmark={false}
            onClick={() => handleFilterClick("activities")}
          />
        </div>
        {filteredItems.length === 0 ? (
          <EmptyState iconName="history" title={`No Recently Visited ${getFilterDisplayName(filter)}`} />
        ) : (
          filteredItems.map((item: RecentlyVisitedItem) => (
            <a href={item.url} key={item.url} className="block" onClick={() => handleRecentItemClick(item)}>
              <div className="flex justify-between items-start pt-2">
                <AvatarDetails
                  picture={item.url.startsWith("/people/") ? item.image : undefined}
                  icon={!item.url.startsWith("/people/") ? (item.image as MaterialNames) : undefined}
                  title={item.text}
                  text={item.subtext}
                />
                <div className="text-body-small text-sys-brand-on-surface-variant whitespace-nowrap">
                  {DateTime.fromISO(String(item.dateTime)).toRelative({ base: DateTime.now() })}
                </div>
              </div>
            </a>
          ))
        )}
      </div>
    </div>
  );
}

export default function QuickSearch({ show, onToggle }: { show: boolean; onToggle(show: boolean): void }) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const canShowCcmsMemberLookup = useCanShowCcmsMemberLookup();
  const indexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;
  const additionalIndex = useMemo(() => createActivityAdditionalSearch(GLOBAL_QUICK_SEARCH_ACTIVITY_SLOTS), []);

  const {
    query,
    displayValue,
    results,
    isLoading,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<GlobalHit>({
    indexName,
    hitsPerPage: 15,
    initialUiState: createGlobalSearchUiState(indexName),
    getDisplayValue: getGlobalDisplayName,
    isExactMatch: isGlobalExactMatch,
    filterHits: filterGlobalSearchHits,
    additionalIndex,
    onSubmit: (q) => {
      navigate(`/search?q=${encodeURIComponent(q)}` as Path);
    },
    onSelect: (hit) => {
      trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchResult, {
        backofficeObjectType: hit.object_type
      });
      navigateGlobalHit(hit, navigate);
    }
  });

  const handleClearRef = useRef(handleClear);
  handleClearRef.current = handleClear;

  useEffect(() => {
    if (!show) {
      handleClearRef.current();
    }
  }, [show]);

  const hasActiveQuery = query.trim() !== "";
  const pgaIdQuery = useMemo(() => parsePgaIdQuery(query), [query]);
  const hasExactPersonMatch = useMemo(
    () => results.some((hit) => hit.object_type === "Person" && isGlobalExactMatch(hit, query)),
    [results, query]
  );
  const showCcmsLookup =
    canShowCcmsMemberLookup && Boolean(pgaIdQuery) && !isLoading && !hasExactPersonMatch && hasActiveQuery;

  const handleCcmsPersonFound = useCallback(
    (pgaId: string) => {
      onToggle(false);
      navigate(`/people/${pgaId}` as Path);
    },
    [navigate, onToggle]
  );

  const trailingIcon =
    displayValue || query ? (
      <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
        <Icon name="cancel" />
      </button>
    ) : undefined;

  return (
    <SideSheetModal isSidebarOpen={show} setSidebarOpen={onToggle} title="Quick Search">
      <div
        className={hasActiveQuery ? "sticky top-0 z-10 bg-sys-brand-surface-container-low pt-4 pb-4" : "mt-4"}
        data-algolia-sticky-search-header="true"
        ref={searchBoxRef}
      >
        <TextField
          label="Quick Search"
          value={displayValue || query}
          onChangeText={(value) => {
            setDisplayValue(undefined);
            handleQueryChange(value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          leadingIcon="search"
          trailingIcon={trailingIcon}
          variant="outlined"
          className="w-full"
        />
      </div>

      {hasActiveQuery ? (
        <AlgoliaSearchResults
          results={results}
          query={query}
          isLoading={isLoading}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={handleItemSelected}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={renderGlobalHit}
          isExactMatch={isGlobalExactMatch}
          noMatchExtra={
            showCcmsLookup && pgaIdQuery ? (
              <LookupPersonInCcms pgaId={pgaIdQuery} onFound={handleCcmsPersonFound} variant="inline" />
            ) : undefined
          }
        />
      ) : (
        <RecentlyVisited />
      )}
    </SideSheetModal>
  );
}
